import React, { useState } from 'react';
import { Segmented, Table, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import type { OrderProduct } from '../../../../types/order';
import { formatCurrencyLocalized } from '../../../../utils/currency';
import { formatLocalizedPercent } from '../../../../utils/numberFormatting';
import { formatCommercialPriceUnit } from '../../commercialPriceUnit';
import { getOrderProductDisplayDimensions, getOrderProductDisplayName } from '../../orderProductDisplay';
import type { OrderStepContext } from '../orderStepContext';
import { HiddenValue, LineValuePending, LineValuesFailedNote } from '../OrderProcessParts';
import OrderQuantityPreview from '../../components/OrderQuantityPreview';
import { OrderLineCostSource } from '../OrderMarginExplanation';
import { ORDER_MARGIN_STAGE_LABEL_KEYS } from '../orderMarginStage';
import { useMarginIncompleteText } from '../OrderProcessHeader';
import { GOODS_SHARE_KEYS, goodsBreakdown, goodsItemSource, goodsTransportPLN, orderQuantityTotals, revenueShares, type SplitUnit } from './orderGoodsBreakdown';
import { formatUnit } from '../../../../utils/units';
import './OrderGuide.css';

import { getOrderMarginCardStage } from './orderMarginCardStage';

export { getOrderMarginCardStage };

/**
 * „Marża zamówienia” of the first mockup (`mcard`): per line the purchase
 * cost with its source, the cost of the goods and the margin, the outgoing
 * transport, and the tiles Przychód / Koszt łącznie / Planowana marża. Every
 * number is the server's plan (`order.margin`, the per-line planned-margin
 * preview, the warehouse summary plan) — nothing is recomputed here. Roles:
 * ADMIN everything; Handlowiec the amounts of the plan of his order and per
 * line the percent and the cost basis, costs read „koszt widzi administrator”;
 * Operacje no margin at all (`marginVisibility`).
 */
export default function OrderMarginCard({ ctx, stale = false }: {
  ctx: OrderStepContext;
  /** Szkic with unsaved line edits: the figures are the last saved plan. */
  stale?: boolean;
}) {
  const { t, i18n } = useTranslation(['orders']);
  const [splitUnit, setSplitUnit] = useState<SplitUnit>('total');
  const { order, margin, marginVisibility, fifoLines } = ctx;
  const incompleteText = useMarginIncompleteText(margin);
  const stage = getOrderMarginCardStage(order.status, order.settledBySettlementInvoice === true);
  const money = (value: number | null | undefined) => (value == null ? '—' : formatCurrencyLocalized(value, 'PLN', i18n.language));
  const percent = (value: number | null | undefined) => (value == null ? '—' : formatLocalizedPercent(value, i18n.language));
  const stageTag = (
    <Tag color={stage === 'result' ? 'green' : stage === 'forecast' ? 'blue' : undefined}>{t(`orders:process.marginCard.stage.${stage}`)}</Tag>
  );

  if (!marginVisibility.showMargin) {
    return (
      <section className="zk-section zk-mcard" data-testid="zk-margin-card">
        <div className="zk-section__header">
          <h3 className="zk-section__title">{t('orders:process.marginCard.title')}</h3>
          <div className="zk-section__aside">{stageTag}</div>
        </div>
        <div className="zk-callout zk-callout--muted zk-callout--flush" role="status">{t('orders:process.hidden.margin')}</div>
      </section>
    );
  }

  const showCosts = marginVisibility.showCosts;
  const products = order.productsLoaded ? order.products : [];
  const finance = ctx.warehouse?.finance;
  const pickup = order.deliveryMode === 'CUSTOMER_PICKUP';
  // Straight from the factory the freight rides on the incoming truck and is part of the goods cost (planned
  // until the PZ, then in the batch); there is no outgoing transport to wait for.
  const factoryDirect = order.fulfillmentSource === 'FACTORY_DIRECT' && !(finance && finance.transportCostPLN > 0);
  const hasPrices = products.length > 0 && products.every((product) => Number(product.unitPrice) > 0);
  const fifoLine = (id: string) => fifoLines.find((line) => line.id === id);
  // A line without its preview answer yet (after a save / status change) reads „liczę…”, never „—”.
  const pending = (id: string) => Boolean(ctx.fifoLoading) && !fifoLine(id);
  const hasCostSource = (id: string) => Boolean(finance?.plan?.lines.some((line) => line.id === id) || fifoLine(id)?.costBasis);
  const basisTag = (basis: string | undefined) => (basis === 'WAREHOUSE'
    ? <Tag color="green">{t('orders:process.marginCard.basis.actual')}</Tag>
    : basis === 'MIXED'
      ? <Tag color="orange">{t('orders:process.marginCard.basis.mixed')}</Tag>
      : basis === 'AVERAGE_ESTIMATE'
        ? <Tag color="orange">{t('orders:process.marginCard.basis.estimate')}</Tag>
        : basis === 'UNKNOWN' ? <Tag color="orange">{t('orders:process.marginCard.basis.unknown')}</Tag> : null);

  const marginTile = (() => {
    if (margin.costState === 'UNKNOWN') return t('orders:process.margin.unknown');
    const value = [margin.amount != null ? money(margin.amount) : null, margin.percent != null ? percent(margin.percent) : null].filter(Boolean).join(' · ');
    if (!hasPrices) return t('orders:process.marginCard.noPrice');
    return value || t('orders:process.margin.unknown');
  })();
  const loss = margin.costState !== 'UNKNOWN' && margin.amount != null && margin.amount < 0;
  const plan = finance?.plan;
  // „Wyjaśnienie marży” reads one source, the live plan, so its rows always add
  // up: revenue − known cost (goods + transport + other) = margin. With part of
  // the cost unknown, the margin is the one without that part.
  const explainedMarginPLN = plan?.revenuePLN == null ? null
    : plan.marginPLN ?? Math.round((plan.revenuePLN - plan.knownCostPLN) * 100) / 100;
  const shares = showCosts && finance && plan && hasPrices && margin.costState !== 'UNKNOWN'
    ? revenueShares({
      revenuePLN: plan.revenuePLN,
      marginPLN: explainedMarginPLN,
      breakdown: goodsBreakdown(plan),
      transportPLN: factoryDirect ? 0 : finance.transportCostPLN,
      otherPLN: finance.otherCostPLN,
    })
    : null;

  return (
    <section className="zk-section zk-mcard" data-testid="zk-margin-card" data-stale={stale ? 'true' : 'false'}>
      <div className="zk-section__header">
        <h3 className="zk-section__title">{t('orders:process.marginCard.title')}</h3>
        <div className="zk-section__aside">{stageTag}</div>
      </div>
      {ctx.fifoFailed && <LineValuesFailedNote />}
      <Table<OrderProduct>
        size="small"
        rowKey="id"
        pagination={false}
        tableLayout="auto"
        dataSource={products}
        locale={{ emptyText: t('orders:process.lines.empty') }}
        columns={[
          {
            title: t('orders:process.marginCard.columns.line'),
            key: 'line',
            render: (_v, product) => (
              <>
                <b>{getOrderProductDisplayName(product, product.name)}</b>
                {getOrderProductDisplayDimensions(product as never) && (
                  <div className="zk-mcard__sub">{getOrderProductDisplayDimensions(product as never)}</div>
                )}
                <div className="zk-mcard__sub"><OrderQuantityPreview line={product} /></div>
              </>
            ),
          },
          {
            title: t('orders:process.marginCard.columns.salePrice'),
            key: 'salePrice',
            align: 'right',
            render: (_v, product) => (Number(product.unitPrice) > 0
              ? (
                <div className="zk-mcard__cost" data-testid="zk-line-sale-price">
                  <b className="zk-num">{money(Number(product.unitPrice))}</b>
                  <div className="zk-mcard__sub">/ {formatCommercialPriceUnit('sale', product, i18n.language)}</div>
                </div>
              )
              : <Tag color="orange">{t('orders:process.marginCard.noPriceTag')}</Tag>),
          },
          {
            title: t('orders:process.marginCard.columns.purchaseCost'),
            key: 'purchaseCost',
            align: 'right',
            render: (_v, product) => {
              const fifo = fifoLine(product.id);
              if (pending(product.id)) return <div className="zk-mcard__cost"><LineValuePending /></div>;
              const source = hasCostSource(product.id);
              // One statement per cell: a price, or the cost source text, or „—” when there is neither.
              return (
                <div className="zk-mcard__cost">
                  {showCosts
                    ? (fifo?.purchaseUnitPrice != null
                      ? <><b className="zk-num">{money(fifo.purchaseUnitPrice)}</b> / {formatCommercialPriceUnit('sale', product, i18n.language)}</>
                      : (source ? null : <span className="zk-num">—</span>))
                    : <HiddenValue reason="costs" />}
                  {fifo?.costBasis && <div className="zk-mcard__sub">{basisTag(fifo.costBasis)}</div>}
                  {source && <div className="zk-mcard__source"><OrderLineCostSource ctx={ctx} product={product} hideBasis={Boolean(fifo?.costBasis)} /></div>}
                </div>
              );
            },
          },
          {
            title: t('orders:process.marginCard.columns.goodsCost'),
            key: 'goodsCost',
            align: 'right',
            render: (_v, product) => {
              if (!showCosts) return <HiddenValue reason="costs" />;
              if (pending(product.id)) return <LineValuePending />;
              const cost = fifoLine(product.id)?.costPLN;
              const freight = goodsTransportPLN(plan?.lines.find((line) => line.id === product.id)?.goodsBreakdown);
              return (
                <>
                  <span className="zk-num">{money(cost)}</span>
                  {freight > 0 && (
                    <div className="zk-mcard__sub zk-mcard__freight" data-testid="zk-line-inbound-freight">
                      {t('orders:process.marginCard.split.lineFreight', { amount: money(freight) })}
                    </div>
                  )}
                </>
              );
            },
          },
          {
            title: t('orders:process.marginCard.columns.margin'),
            key: 'margin',
            align: 'right',
            render: (_v, product) => {
              const fifo = fifoLine(product.id);
              if (pending(product.id)) return <LineValuePending />;
              if (!fifo || (showCosts ? fifo.marginPLN == null : fifo.marginPercent == null)) return <span className="zk-num">—</span>;
              const negative = showCosts ? Number(fifo.marginPLN) < 0 : Number(fifo.marginPercent) < 0;
              return (
                <span className={`zk-num${negative ? ' zk-mcard__neg' : ''}`} data-testid="zk-line-margin">
                  {showCosts && <b>{money(fifo.marginPLN)}<br /></b>}
                  <span className="zk-mcard__sub">{percent(fifo.marginPercent)}</span>
                </span>
              );
            },
          },
        ]}
        summary={() => (showCosts && finance ? (
          <Table.Summary>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={3}>
                <b>{t(factoryDirect ? 'orders:process.marginCard.transportFactoryTitle' : 'orders:process.margin.transport')}</b>
                <div className="zk-mcard__sub">
                  {factoryDirect ? t('orders:process.marginCard.transportFactory') : pickup ? t('orders:process.marginCard.transportNone') : finance.transportCostPLN > 0 ? t('orders:process.marginCard.transportKnown') : t('orders:process.marginCard.transportUnknown')}
                </div>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="right">
                {factoryDirect
                  ? (plan && goodsTransportPLN(goodsBreakdown(plan)) > 0
                    ? <span className="zk-num zk-mcard__freight" data-testid="zk-factory-freight">{t('orders:process.marginCard.transportFactoryIncluded', { amount: money(goodsTransportPLN(goodsBreakdown(plan))) })}</span>
                    : <span className="zk-num">—</span>)
                  : <span className="zk-num">{pickup || finance.transportCostPLN > 0 ? money(finance.transportCostPLN) : '—'}</span>}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} />
            </Table.Summary.Row>
            {finance.otherCostPLN > 0 && (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={3}><b>{t('orders:process.margin.other')}</b></Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right"><span className="zk-num">{money(finance.otherCostPLN)}</span></Table.Summary.Cell>
                <Table.Summary.Cell index={4} />
              </Table.Summary.Row>
            )}
          </Table.Summary>
        ) : null)}
      />
      {shares && (() => {
        const goods = shares.filter((share) => GOODS_SHARE_KEYS.includes(share.key));
        const rest = shares.filter((share) => !GOODS_SHARE_KEYS.includes(share.key) && share.key !== 'margin');
        const marginShare = shares.find((share) => share.key === 'margin');
        const revenuePLN = plan!.revenuePLN as number;
        const goodsPLN = Math.round(goods.reduce((total, share) => total + share.amountPLN, 0) * 100) / 100;
        const goodsWidth = goods.reduce((total, share) => total + share.width, 0);
        // The goods cost always shows what it consists of: towar, transport, koszty dodatkowe.
        const showGoodsParts = goods.length > 0;
        const goodsLabel = (key: string) => (key === 'goodsItem'
          ? t(`orders:process.marginCard.split.goodsItem.${goodsItemSource(goodsBreakdown(plan!))}`)
          : t(`orders:process.marginCard.split.${key}`));
        const unitTotals = orderQuantityTotals(products);
        const unitOptions = (['pcs', 'm2', 'm3'] as const).filter((unit) => unitTotals[unit] != null);
        const activeUnit = splitUnit !== 'total' && unitTotals[splitUnit] != null ? splitUnit : 'total';
        const shown = (amountPLN: number) => (activeUnit === 'total'
          ? money(amountPLN)
          : `${money(amountPLN / (unitTotals[activeUnit] as number))} / ${formatUnit(activeUnit, 'pl')}`);
        const partial = margin.costState === 'PARTIAL';
        const row = (key: string, label: string, amountPLN: number | null, pct: number | null, width: number, variant = '', sign = '−') => (
          <div key={key} className={`zk-msplit__row${variant ? ` zk-msplit__row--${variant}` : ''}`}>
            <span className="zk-msplit__label"><span className="zk-msplit__sign" aria-hidden="true">{sign}</span>{label}</span>
            <span className="zk-msplit__track"><span className={`zk-msplit__fill zk-msplit--${key}`} style={{ width: `${width > 0 ? Math.max(width * 100, 0.5) : 0}%` }} /></span>
            <b className="zk-num">{amountPLN == null ? '—' : shown(amountPLN)}</b>
            <span className="zk-msplit__pct">{pct == null ? '' : percent(pct)}</span>
          </div>
        );
        return (
          <div className="zk-msplit" data-testid="zk-revenue-split">
            <div className="zk-msplit__head">
              <div className="zk-msplit__title">{t('orders:process.marginCard.split.title')}</div>
              {unitOptions.length > 0 && (
                <Segmented
                  size="small"
                  value={activeUnit}
                  onChange={(value) => setSplitUnit(value as SplitUnit)}
                  options={[
                    { value: 'total', label: t('orders:process.marginCard.split.unitTotal') },
                    ...unitOptions.map((unit) => ({ value: unit, label: t('orders:process.marginCard.split.unitPer', { unit: formatUnit(unit, 'pl') }) })),
                  ]}
                  data-testid="zk-revenue-split-unit"
                />
              )}
            </div>
            {row('revenue', t('orders:process.marginCard.revenue'), revenuePLN, 100, 1, 'revenue', '')}
            {goods.length > 0 && row('goods', t('orders:process.marginCard.split.goods'), goodsPLN,
              Math.round(goodsPLN / revenuePLN * 1000) / 10, goodsWidth, 'group')}
            {showGoodsParts && goods.map((share) => row(share.key, goodsLabel(share.key), share.amountPLN, share.percent, share.width,
              share.key === 'goodsTransport' ? 'child zk-msplit__row--freight' : 'child'))}
            {rest.map((share) => row(share.key, t(`orders:process.marginCard.split.${share.key}`), share.amountPLN, share.percent, share.width,
              share.key === 'outboundTransport' ? 'freight' : ''))}
            {partial && row('unknown', t('orders:process.marginCard.split.unknownCost'), null, null, 0, 'unknown')}
            {row('margin', t(partial ? 'orders:process.marginCard.split.marginPartial' : 'orders:process.marginCard.split.margin'),
              explainedMarginPLN, partial ? null : plan!.marginPercent, marginShare?.width ?? 0,
              Number(explainedMarginPLN) < 0 ? 'margin zk-msplit__row--loss' : 'margin', '=')}
            <div className="zk-msplit__note" data-testid="zk-revenue-split-note">{t('orders:process.marginCard.split.cogsNote')}</div>
          </div>
        );
      })()}
      <div className="zk-msum" data-testid="zk-margin-tiles">
        <div>
          <span>{t('orders:process.marginCard.revenue')}</span>
          <b className="zk-num">{money(order.revenue)}</b>
        </div>
        <div>
          <span>{t('orders:process.marginCard.totalCost')}</span>
          <b className="zk-num">
            {showCosts
              ? (margin.costState === 'UNKNOWN' ? t('orders:process.margin.unknown') : money(order.totalCost))
              : <HiddenValue reason="costs" />}
          </b>
        </div>
        <div className={`zk-msum__big${loss ? ' zk-msum__big--loss' : ''}`} data-cost-state={margin.costState}>
          <span>{t(ORDER_MARGIN_STAGE_LABEL_KEYS[margin.stage])}{margin.provisional && ` · ${t('orders:process.margin.provisionalShort')}`}</span>
          <b className="zk-num" data-testid="zk-margin-total">{marginTile}</b>
          {stale && <span className="zk-msum__note" data-testid="zk-margin-card-stale">{t('orders:process.margin.staleUntilSave')}</span>}
          {incompleteText && <span className="zk-msum__note" title={incompleteText}>{incompleteText}</span>}
        </div>
      </div>
      <p className="zk-section__hint zk-mcard__foot">{t(stage === 'before' ? 'orders:process.marginCard.footBefore' : 'orders:process.marginCard.footConfirmed')}</p>
    </section>
  );
}
